import React, { useEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { getStyles } from './styles';
import { Box, Button, FormControl, FormControlLabel, FormGroup, Radio, RadioGroup, Stack, TextField, Typography } from '@mui/material';
import { ReactSVG } from 'react-svg';
import { IconSvg } from '../../../../utils/globalIcons';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppContext } from '../../../../context/AppContext';
import LoadBar from '../../../../utils/LoadBar';
import FilesApi from '../../../../Api/Services/AppflyteBackend/FileServiceApi';
import { ANALYTICS_S3_BUCKET_FOLDER } from '../../../../utils/constants';

let s3_foldername = ANALYTICS_S3_BUCKET_FOLDER
let bucket_name = window.__ENV__.S3_BUCKET_NAME

function GeneralSettings({ dataLoading, action_type, onSave, onCancel, onNext }) {
  const theme = useTheme();
  const styles = getStyles(theme);
  const navigate = useNavigate();
  const location = useLocation();

  const { setAppDetails, appDetails, appLogoDetails } = useAppContext();
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    const path = location.pathname ?? null;
    if (!path || (!path.startsWith('/create-ai-app') && !path.startsWith('/edit-ai-app'))) {
      navigate('/ai-apps');
    }
    const app_logo_name = appDetails?.appLogoData?.s3_url ?? null;
    const has_preview = appDetails?.appLogoData?.previewUrl ?? null;
    if (app_logo_name && !has_preview) {
      fetchLogoData(app_logo_name)
    }
  }, [location, appDetails])

  useEffect(() => {
    const errors = { ...formErrors };
    if (formErrors?.app_name && appDetails?.app_name?.trim()) { delete errors?.app_name }
    if (formErrors?.previewUrl && appDetails?.previewUrl) { delete errors?.previewUrl }
    if (JSON.stringify(errors) !== JSON.stringify(formErrors)) { setFormErrors(errors) }
  }, [appDetails, formErrors]);

  const fetchLogoData = async (app_logo_path) => {
    try {
      if (app_logo_path) {
        const response = await FilesApi.getDownloadUrl(bucket_name, app_logo_path)
        if (response.status === 200) {
          const response_data = response?.data?.at(-1) ?? null;
          const download_url = Object.values(response_data)?.at(-1) ?? null;
          if (download_url) {
            setAppDetails((prev) => ({
              ...prev,
              appLogoData: {
                ...prev.appLogoData,
                previewUrl: download_url
              }
            }));
          }
        }
      }
    } catch (error) {
      console.log(error)
    }
  }

  const handleBotTypeChange = (event) => {
    const selectedType = event.target.value;
    setAppDetails((prev) => ({ ...prev, app_bot_type: selectedType }));
    if (selectedType === "simple_bot") {
      setAppDetails((prev) => ({
        ...prev,
        authConfigData: {
          ...prev.authConfigData,
          auth_type: 'no_auth'
        }
      }));
      return;
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setAppDetails((prev) => ({
        ...prev, appLogoData: {
          ...prev.appLogoData,
          selectedLogo: file,
          previewUrl: URL.createObjectURL(file),
          s3_url: null
        }
      }))
    }
  };

  const handleFileRemove = () => {
    setAppDetails((prev) => ({ ...prev, appLogoData: appLogoDetails }))
  }

  const handleNext = () => {
    try {
      const errors = {};
      if (!appDetails?.app_name?.trim()) { errors.app_name = 'This filed is required' }
      if (Object.keys(errors).length === 0) {
        onNext('datasets')
      }
      else {
        setFormErrors(errors);
      }
    }
    catch (err) {
      console.error('Error during navigation:', err);
    }
  }

  return (
    <Box sx={styles.mainContainer}>
      <Box sx={styles.cardContainer}>

        {dataLoading && <LoadBar />}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={{ fontSize: '16px', fontWeight: '600' }}>Create Answering Bot AI App</Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Button
              disabled={dataLoading}
              sx={{ backgroundColor: '#0B51C5', borderRadius: '20px', width: '100px' }}
              onClick={() => action_type === 'update' ? onSave('general') : handleNext()}
            >
              <Typography sx={{ fontSize: '14px', fontWeight: '600', color: '#FFFFFF' }}>
                {action_type === 'update' ? 'SAVE' : 'NEXT'}
              </Typography>
            </Button>
            <Button
              sx={{ backgroundColor: '#DEDEDE', borderRadius: '20px', width: '100px' }}
              onClick={onCancel}
            >
              <Typography sx={{ fontSize: '14px', fontWeight: '600', color: '#404040' }}>
                CANCEL
              </Typography>
            </Button>
          </Box>
        </Box>

        <Box marginTop={'20px'}>
          <FormGroup>
            <FormControl>
              <Typography mb={0.5}>App Name <span style={{ color: 'red' }}>*</span></Typography>
              <TextField
                variant="outlined"
                type="text"
                size='small'
                sx={{ width: '500px' }}
                value={appDetails.app_name}
                onChange={(e) => setAppDetails({ ...appDetails, app_name: e.target.value })}
              />
              {formErrors.app_name && <Typography sx={styles.errorText}>{formErrors.app_name}</Typography>}
            </FormControl>

            <FormControl sx={{ marginTop: '20px' }}>
              <Typography mb={0.5}>App Description</Typography>
              <TextField
                variant="outlined"
                type="text"
                size='medium'
                sx={{ width: '500px' }}
                value={appDetails.app_description}
                onChange={(e) => setAppDetails({ ...appDetails, app_description: e.target.value })}
              />
              {formErrors.app_description && <Typography sx={styles.errorText}>{formErrors.app_description}</Typography>}
            </FormControl>

            <FormControl sx={{ marginTop: '20px' }}>
              <Typography mb={0.5}>App Icon</Typography>

              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box
                  onDragOver={(e) => e.preventDefault()}
                  sx={styles.fileUploadContainer}
                >
                  {!appDetails?.appLogoData?.previewUrl && (
                    <label
                      style={{
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "100%",
                        height: "100%",
                        transition: "background 0.2s ease-in-out",
                        position: "absolute",
                        inset: 0,
                      }}
                    >
                      <ReactSVG
                        src={IconSvg.fileUploadIcon}
                        beforeInjection={(svg) => {
                          svg.setAttribute('style', 'width:24px; height:24px; display:block;');
                          svg.setAttribute('stroke', '#1976d2');
                        }}
                      />
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        style={{
                          position: "absolute",
                          inset: 0,
                          width: "100%",
                          height: "100%",
                          opacity: 0,
                          cursor: "pointer",
                        }}
                      />

                    </label>
                  )}

                  {appDetails?.appLogoData?.previewUrl && (
                    <Box
                      sx={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        overflow: "hidden",
                      }}
                    >
                      {(appDetails?.appLogoData?.selectedLogo?.type === "image/svg+xml" ||
                        (appDetails?.appLogoData?.s3_url ?? '').toLowerCase().endsWith('.svg')) ?
                        (<ReactSVG
                          src={appDetails?.appLogoData?.previewUrl}
                          beforeInjection={(svg) => {
                            svg.setAttribute("style", "width:100%; height:100%; object-fit:contain;");
                          }}
                        />)
                        :
                        (<Box
                          sx={{
                            width: "500px",
                            height: "100%",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          <img
                            src={appDetails?.appLogoData?.previewUrl}
                            alt="App Logo Preview"
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "contain",
                            }}
                          />
                        </Box>)
                      }
                    </Box>
                  )}

                </Box>

                {appDetails?.appLogoData?.previewUrl && (
                  <Box height={'15%'} marginLeft={'10px'}>
                    <Box onClick={handleFileRemove}>
                      <ReactSVG src={IconSvg.deleteIcon} className='delete_icon' />
                    </Box>
                  </Box>)
                }
              </Box>
              {(formErrors.previewUrl || formErrors.selectedLogo) && <Typography sx={styles.errorText}>{formErrors.previewUrl}</Typography>}

            </FormControl>


            <FormControl sx={{ marginTop: '20px' }}>
              <Typography mb={0.5}>Bot Type</Typography>

              <RadioGroup
                aria-labelledby="bot-type-selection"
                value={appDetails?.app_bot_type}
                onChange={(e) => handleBotTypeChange(e)}
              >
                <Box
                  sx={{
                    bgcolor: "#FFFFFF",
                    borderRadius: "5px",
                    border: "1px solid #DEDEDE",
                    cursor: "pointer",
                    width: "500px",
                    p: 1.5,
                    mb: 1,
                    "&:hover": { borderColor: "#59A292" },
                  }}
                >
                  <FormControlLabel
                    value="simple_bot"
                    control={
                      <Radio
                        sx={{
                          color: "#59A292",
                          "&.Mui-checked": { color: "#59A292" },
                        }}
                      />
                    }
                    label={
                      <Box sx={{ display: "flex", flexDirection: "column" }}>
                        <Typography
                          sx={{
                            fontWeight: "400",
                            fontSize: "14px",
                          }}
                        >
                          Simple
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: "400",
                            fontSize: "12px",
                          }}
                        >
                          Floating with no chat history and does not support authentication
                        </Typography>
                      </Box>
                    }
                  />
                </Box>

                <Box
                  sx={{
                    bgcolor: "#FFFFFF",
                    borderRadius: "5px",
                    border: "1px solid #DEDEDE",
                    cursor: "pointer",
                    width: "500px",
                    p: 1.5,
                    "&:hover": { borderColor: "#59A292" },
                  }}
                >
                  <FormControlLabel
                    value="advanced_bot"
                    control={
                      <Radio
                        sx={{
                          color: "#59A292",
                          "&.Mui-checked": { color: "#59A292" },
                        }}
                      />
                    }
                    label={
                      <Box sx={{ display: "flex", flexDirection: "column" }}>
                        <Typography
                          sx={{
                            fontWeight: "400",
                            fontSize: "14px",
                          }}
                        >
                          Advanced
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: "400",
                            fontSize: "12px",
                          }}
                        >
                          Full screen with user authentication, bookmarks and chat history
                        </Typography>
                      </Box>
                    }
                  />
                </Box>
              </RadioGroup>
            </FormControl>

          </FormGroup>
        </Box>



      </Box>
    </Box>
  )
}

export default GeneralSettings;